(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.Axiom && typeof window.Axiom.formatMoney === 'function') return window.Axiom.formatMoney(cents);
    return (Number(cents || 0) / 100).toFixed(2);
  }

  class BundleProduct extends HTMLElement {
    connectedCallback() {
      if (this._bundleReady) return;
      this._bundleReady = true;
      this._onChange = this._onChange.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onDocumentPointerDown = this._onDocumentPointerDown.bind(this);
      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('pointerdown', this._onDocumentPointerDown);
      this._initialize();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('pointerdown', this._onDocumentPointerDown);
      this._bundleReady = false;
    }

    _initialize() {
      this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.discountPercent = Math.max(0, parseFloat(this.dataset.bundleDiscount) || 0);
      this.items.forEach((item) => this._prepareItem(item));
      this._updateSummary();
    }

    _prepareItem(item) {
      const script = item.querySelector('[data-bundle-variants]');
      let variants = [];
      try {
        variants = script ? JSON.parse(script.textContent || '[]') : [];
      } catch (error) {
        variants = [];
      }
      item._bundleVariants = variants;
      const selects = Array.from(item.querySelectorAll('[data-bundle-option-select]'));
      let variant = this._resolveVariant(item, selects);
      if (!variant) variant = variants.find((entry) => entry.available) || variants[0] || null;
      item._bundleVariant = variant;
      this._setVariant(item, variant, false);
      this._updateToggleLabel(item);
    }

    _resolveVariant(item, selects) {
      const variants = item._bundleVariants || [];
      if (!variants.length) return null;
      const values = selects.map((select) => select.value);
      const exact = variants.find((variant) => (variant.options || []).every((value, index) => value === values[index]));
      if (exact) return exact;
      return variants.find((variant) => variant.available) || variants[0] || null;
    }

    _setVariant(item, variant, announce) {
      if (!variant) return;
      item._bundleVariant = variant;
      item.dataset.bundleVariantId = variant.id || '';
      const selects = Array.from(item.querySelectorAll('[data-bundle-option-select]'));
      selects.forEach((select, index) => {
        const value = variant.options && variant.options[index];
        if (value != null && select.value !== value) select.value = value;
        this._syncOptionDropdown(select);
      });
      const checkbox = item.querySelector('[data-bundle-include]');
      const available = Boolean(variant.available);
      item.classList.toggle('is-unavailable', !available);
      if (checkbox) {
        checkbox.disabled = !available;
        if (!available) checkbox.checked = false;
      }
      const currentPrice = item.querySelector('[data-bundle-current-price]');
      const comparePrice = item.querySelector('[data-bundle-compare-price]');
      if (currentPrice) currentPrice.textContent = formatMoney(variant.price || 0);
      if (comparePrice) {
        comparePrice.textContent = formatMoney(variant.compare_at_price || 0);
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
      }
      this._updateToggleLabel(item);
      if (announce && !available) this._announce(this.dataset.bundleSelectMessage || 'Select an available variant.');
    }

    _syncOptionDropdown(select) {
      if (!select) return;
      const dropdown = select.closest('[data-bundle-option-dropdown]');
      if (!dropdown) return;
      const current = dropdown.querySelector('[data-bundle-option-current]');
      const selectedOption = select.options[select.selectedIndex];
      if (current) current.textContent = selectedOption ? selectedOption.textContent : select.value;
      dropdown.querySelectorAll('[data-bundle-option-value]').forEach((option) => {
        const selected = option.dataset.bundleOptionValue === select.value;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
    }

    _resetOptionDropdownReveal(panel) {
      if (!panel) return;
      panel.removeAttribute('data-reveal-state');
      panel.querySelectorAll('[data-reveal-item]').forEach((item) => {
        item.removeAttribute('data-reveal-ready');
        item.removeAttribute('data-reveal-state');
        item.style.removeProperty('--reveal-calculated-delay');
      });
    }

    _closeOptionDropdown(dropdown, returnFocus) {
      if (!dropdown) return;
      const trigger = dropdown.querySelector('[data-bundle-option-trigger]');
      const panel = dropdown.querySelector('[data-bundle-option-panel]');
      dropdown.classList.remove('is-open', 'bundle-product-item__option--open-up');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) {
        panel.hidden = true;
        panel.style.removeProperty('--bundle-option-panel-max-height');
      }
      this._resetOptionDropdownReveal(panel);
      if (returnFocus && trigger) trigger.focus({ preventScroll: true });
    }

    _closeOptionDropdowns(except, returnFocus) {
      this.querySelectorAll('[data-bundle-option-dropdown].is-open').forEach((dropdown) => {
        if (dropdown !== except) this._closeOptionDropdown(dropdown, returnFocus);
      });
    }

    _openOptionDropdown(dropdown, focusTarget) {
      if (!dropdown) return;
      const trigger = dropdown.querySelector('[data-bundle-option-trigger]');
      const panel = dropdown.querySelector('[data-bundle-option-panel]');
      if (!trigger || !panel) return;
      this._closeOptionDropdowns(dropdown, false);
      dropdown.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this._positionOptionDropdown(dropdown, trigger, panel);
      if (window.Axiom && typeof window.Axiom.revealElements === 'function') {
        window.Axiom.revealElements(panel, {
          trigger: 'open',
          selector: '[data-reveal-item]',
          reset: true,
          stagger: 45
        });
      }
      if (!focusTarget) return;
      const options = Array.from(panel.querySelectorAll('[data-bundle-option-value]'));
      const selected = panel.querySelector('[aria-selected="true"]');
      const target = focusTarget === 'last' ? options[options.length - 1] : selected || options[0];
      if (target) target.focus({ preventScroll: true });
    }

    _positionOptionDropdown(dropdown, trigger, panel) {
      dropdown.classList.remove('bundle-product-item__option--open-up');
      panel.style.removeProperty('--bundle-option-panel-max-height');

      const section = dropdown.closest('.bundle-product-section') || this;
      const sectionRect = section.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const panelHeight = Math.min(panel.scrollHeight, 320);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const headerHeight = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--axiom-header-height')
      ) || 0;
      const boundaryTop = Math.max(headerHeight, sectionRect.top);
      const boundaryBottom = Math.min(viewportHeight, sectionRect.bottom);
      const panelGap = 6;
      const spaceAbove = Math.max(0, triggerRect.top - boundaryTop - panelGap);
      const spaceBelow = Math.max(0, boundaryBottom - triggerRect.bottom - panelGap);
      const openUp = panelHeight > spaceBelow && spaceAbove > spaceBelow;
      const availableSpace = openUp ? spaceAbove : spaceBelow;

      dropdown.classList.toggle('bundle-product-item__option--open-up', openUp);
      panel.style.setProperty('--bundle-option-panel-max-height', `${Math.max(48, Math.floor(availableSpace))}px`);
    }

    _onDocumentPointerDown(event) {
      const dropdown = event.target.closest && event.target.closest('[data-bundle-option-dropdown]');
      if (!this.contains(event.target)) {
        this._closeOptionDropdowns(null, false);
        return;
      }
      this._closeOptionDropdowns(dropdown, false);
    }

    _updateToggleLabel(item) {
      const checkbox = item.querySelector('[data-bundle-include]');
      const label = item.querySelector('[data-bundle-toggle-label]');
      if (!checkbox || !label) return;
      label.textContent = checkbox.checked ? (this.dataset.bundleIncludedLabel || 'Included') : (this.dataset.bundleRemovedLabel || 'Removed');
    }

    _onChange(event) {
      const option = event.target.closest('[data-bundle-option-select]');
      if (option) {
        const item = option.closest('[data-bundle-item]');
        if (item) {
          this._setVariant(item, this._resolveVariant(item, Array.from(item.querySelectorAll('[data-bundle-option-select]'))), true);
          this._updateSummary();
        }
        return;
      }
      const checkbox = event.target.closest('[data-bundle-include]');
      if (!checkbox) return;
      const item = checkbox.closest('[data-bundle-item]');
      if (!item) return;
      item.dataset.bundleSelected = checkbox.checked ? 'true' : 'false';
      this._updateToggleLabel(item);
      this._updateSummary();
    }

    _onClick(event) {
      const optionTrigger = event.target.closest('[data-bundle-option-trigger]');
      if (optionTrigger) {
        event.preventDefault();
        const dropdown = optionTrigger.closest('[data-bundle-option-dropdown]');
        if (dropdown.classList.contains('is-open')) {
          this._closeOptionDropdown(dropdown, false);
        } else {
          this._openOptionDropdown(dropdown, false);
        }
        return;
      }
      const optionValue = event.target.closest('[data-bundle-option-value]');
      if (optionValue) {
        event.preventDefault();
        const dropdown = optionValue.closest('[data-bundle-option-dropdown]');
        const select = dropdown && dropdown.querySelector('[data-bundle-option-select]');
        if (select && select.value !== optionValue.dataset.bundleOptionValue) {
          select.value = optionValue.dataset.bundleOptionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this._closeOptionDropdown(dropdown, true);
        return;
      }
      const disclosure = event.target.closest('[data-bundle-disclosure]');
      if (disclosure) {
        event.preventDefault();
        const expanded = this.classList.toggle('bundle-product--summary-expanded');
        disclosure.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const icon = disclosure.querySelector('.bundle-product-summary__disclosure-icon');
        if (icon) icon.textContent = expanded ? '−' : '+';
        return;
      }
      const button = event.target.closest('[data-bundle-submit]');
      if (!button || button.disabled) return;
      event.preventDefault();
      const items = this._selectedItems();
      if (!items.length) return;
      const payload = items.map((item) => ({ id: item.dataset.bundleVariantId, quantity: 1 })).filter((item) => item.id);
      if (!payload.length || !window.AxiomStandardStorefront || typeof window.AxiomStandardStorefront.addLines !== 'function') return;
      button.disabled = true;
      this._announce(this.dataset.bundleAddingMessage || 'Adding bundle.');
      window.AxiomStandardStorefront.addLines(payload, { target: this, source: 'bundle-product' }).catch(() => {
        this._announce(this.dataset.bundleAddMessage || 'Could not add bundle to cart.');
      }).finally(() => {
        button.disabled = false;
      });
    }

    _onKeydown(event) {
      const trigger = event.target.closest('[data-bundle-option-trigger]');
      if (trigger) {
        const dropdown = trigger.closest('[data-bundle-option-dropdown]');
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this._openOptionDropdown(dropdown, event.key === 'ArrowUp' ? 'last' : 'selected');
        } else if (event.key === 'Escape' && dropdown.classList.contains('is-open')) {
          event.preventDefault();
          this._closeOptionDropdown(dropdown, true);
        }
        return;
      }

      const option = event.target.closest('[data-bundle-option-value]');
      if (!option) return;
      const dropdown = option.closest('[data-bundle-option-dropdown]');
      const panel = option.closest('[data-bundle-option-panel]');
      const options = Array.from(panel.querySelectorAll('[data-bundle-option-value]'));
      const index = options.indexOf(option);
      let nextIndex = index;

      if (event.key === 'ArrowDown') nextIndex = Math.min(options.length - 1, index + 1);
      else if (event.key === 'ArrowUp') nextIndex = Math.max(0, index - 1);
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = options.length - 1;
      else if (event.key === 'Escape') {
        event.preventDefault();
        this._closeOptionDropdown(dropdown, true);
        return;
      } else if (event.key === 'Tab') {
        this._closeOptionDropdown(dropdown, false);
        return;
      } else {
        return;
      }

      event.preventDefault();
      if (options[nextIndex]) options[nextIndex].focus({ preventScroll: true });
    }

    _selectedItems() {
      return this.items.filter((item) => {
        const checkbox = item.querySelector('[data-bundle-include]');
        return checkbox && checkbox.checked && !checkbox.disabled && item.dataset.bundleVariantId;
      });
    }

    _updateSummary() {
      const selected = this._selectedItems();
      const subtotal = selected.reduce((sum, item) => sum + Number(item._bundleVariant && item._bundleVariant.price || 0), 0);
      const discount = selected.length ? Math.round(subtotal * this.discountPercent / 100) : 0;
      const total = subtotal - discount;
      const setText = (selector, value) => {
        const element = this.querySelector(selector);
        if (element) element.textContent = value;
      };
      setText('[data-bundle-selected-count]', selected.length + ' / ' + this.items.length);
      setText('[data-bundle-subtotal]', formatMoney(subtotal));
      setText('[data-bundle-discount-value]', discount ? '−' + formatMoney(discount) : '—');
      setText('[data-bundle-total]', formatMoney(total));
      setText('[data-bundle-mobile-total]', formatMoney(total));
      const summaryHeading = this.querySelector('[data-bundle-summary-heading]');
      if (summaryHeading) {
        summaryHeading.textContent = summaryHeading.dataset.headingTemplate.split('[count]').join(String(selected.length));
      }
      const button = this.querySelector('[data-bundle-submit]');
      if (button) button.disabled = selected.length === 0;
    }

    _announce(text) {
      const live = this.querySelector('[data-bundle-live]');
      if (live) live.textContent = text;
    }
  }

  if (!customElements.get('bundle-product')) customElements.define('bundle-product', BundleProduct);
})();
